import { sanitizeFilePart } from './reconFilesUpload';

export const AVAILABILITY = ['In Stock', '0-1 Days', '1-2 Days', '2-3 Days', '3+ Days'];

export const num = (raw, nullable = false) => {
  const s = String(raw ?? '').trim();
  if (s === '') return nullable ? null : 0;
  const n = parseFloat(s);
  return Number.isNaN(n) ? (nullable ? null : 0) : n;
};

export const computeTotal = (price, quantity) => Number(price || 0) * Number(quantity || 0);

export const money = (v) => {
  const n = Number(v || 0);
  return Number.isNaN(n) ? '0.00' : n.toFixed(2);
};

export const emptyDraft = {
  catalog_part_id: null,
  part_number: '',
  description: '',
  price: '',
  quantity: 1,
  availability: 'In Stock',
};

export const livingParts = parts => (parts || []).filter(p => !p.deleted);

export const partsForVehicle = active_vehicle => livingParts(active_vehicle?.work_item_parts);

export const partsForMpiWorkItem = (active_vehicle, mpi_work_item_id) => partsForVehicle(active_vehicle)
  .filter(p => p.work_item_source === 'new_work_items' && p.mpi_work_item_id === mpi_work_item_id);

export const partsForLegacyWorkItem = (active_vehicle, work_item_id) => partsForVehicle(active_vehicle)
  .filter(p => p.work_item_source === 'legacy_work_items' && p.work_item_id === work_item_id);

export const partsForAppraisal = (active_vehicle, appraisal_id) => partsForVehicle(active_vehicle)
  .filter(p => p.work_item_source === 'appraisals' && String(p.appraisal_id) === String(appraisal_id));

export const partsForVehicleScope = active_vehicle => partsForVehicle(active_vehicle)
  .filter(p => p.work_item_source === 'vehicle');

export const lineTotal = p => computeTotal(p?.price, p?.quantity);

export const partsTotals = (parts) => {
  const living = livingParts(parts);
  const counted = living.filter(p => !p.declined);
  const sum = (rows, fn) => rows.reduce((acc, p) => acc + Number(fn(p) || 0), 0);
  return {
    total: sum(counted, lineTotal),
    approvedTotal: sum(counted.filter(p => p.approved), lineTotal),
    pendingTotal: sum(counted.filter(p => !p.approved), lineTotal),
    declinedCount: living.filter(p => p.declined).length,
    lineCount: counted.length,
  };
};

export const approvedPartsTotalForMpiWorkItem = (active_vehicle, mpi_work_item_id) => partsForMpiWorkItem(active_vehicle, mpi_work_item_id)
  .filter(p => p.approved && !p.declined)
  .reduce((acc, p) => acc + lineTotal(p), 0);

export const vehiclePartsTotal = active_vehicle => partsTotals(partsForVehicleScope(active_vehicle)).total;

export const CATALOG_THUMBNAIL_CATEGORY = 'catalog_thumbnail';

export const isThumbnailAttachment = a => String(a?.category || '').trim().toLowerCase() === CATALOG_THUMBNAIL_CATEGORY;

const escapeRegExp = s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const isThumbnailNamed = (part_number, attachment) => {
  if (!part_number) return false;
  const name = attachment?.fileid || attachment?.filename || '';
  return new RegExp(`^${escapeRegExp(sanitizeFilePart(part_number))}_\\d{8}(\\.|$)`, 'i').test(name);
};

export const isThumbnailFor = (part, attachment) => isThumbnailAttachment(attachment)
  || isThumbnailNamed(part?.part_number, attachment);

export const partFileAttachments = (attachments, work_item_part_id) => (attachments || [])
  .filter(a => a.work_item_part_id === work_item_part_id && !isThumbnailAttachment(a));

export const catalogPartThumbnail = (attachments, catalog_part_id) => (attachments || [])
  .find(a => a.catalog_part_id === catalog_part_id && isThumbnailAttachment(a));

export const thumbnailOf = part => (part?.attachments || []).find(a => isThumbnailFor(part, a));

export const catalogFilesOf = part => (part?.attachments || []).filter(a => !isThumbnailFor(part, a));

export const dealershipsOf = value => String(value || '').split(',').map(s => s.trim()).filter(Boolean);

export const catalogPartInScope = (part, subdomain) => {
  if (!subdomain) return true;
  const owners = dealershipsOf(part?.dealership);
  return owners.length === 0 || owners.includes(subdomain);
};

export const SUPPORT_USER_NAME = 'iPacketRecon Support';

export const resolveUserName = (users, uid) => {
  if (uid === undefined || uid === null || uid === '' || uid === 0 || uid === '0') return '';
  const list = Array.isArray(users) ? users : Object.values(users || {});
  const u = list.find(x => String(x.id) === String(uid));
  if (!u) return SUPPORT_USER_NAME;
  const source = u.email || u.user_name || '';
  const local = String(source).split('@')[0].trim();
  return local || SUPPORT_USER_NAME;
};

export const isAdminUser = current_user => ['admin', 'super_admin']
  .includes(String(current_user?.position || '').toLowerCase());

export const canDeleteAttachment = (current_user, attachment) => {
  if (isAdminUser(current_user)) return true;
  const uid = current_user?.user;
  if (uid === undefined || uid === null || uid === '') return false;
  return String(attachment?.uid) === String(uid);
};

export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

export const attachmentName = a => a?.fileid || a?.filename || '';

export const isImageAttachment = a => IMAGE_EXTENSIONS
  .includes(String(attachmentName(a)).split('.').pop().toLowerCase());

export const catalogAttachmentCount = part => (part?.attachments || []).length;

export const catalogDisplayImage = (part) => {
  const explicit = thumbnailOf(part);
  if (explicit) return { attachment: explicit, isOfficial: true };
  const anyImage = (part?.attachments || []).find(isImageAttachment);
  return anyImage ? { attachment: anyImage, isOfficial: false } : null;
};

export const presetPartLinks = preset => [...(preset?.work_item_preset_parts || [])]
  .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

export const presetPartsToItems = (preset, catalog_parts, basePayload, uid) => {
  const byId = new Map((catalog_parts || []).map(c => [String(c.id), c]));
  return presetPartLinks(preset).map((link) => {
    const cat = byId.get(String(link.catalog_part_id));
    if (!cat) return null;
    const price = num(cat.price);
    const quantity = num(link.default_quantity) || 1;
    const item = {
      ...basePayload,
      catalog_part_id: cat.id,
      part_number: cat.part_number,
      description: cat.description || '',
      price,
      quantity,
      availability: '',
      total: computeTotal(price, quantity),
    };
    if (link.auto_approve) {
      item.approved = true;
      item.approved_by_date = new Date().toISOString();
    }
    return item;
  }).filter(Boolean);
};

export const PARTS_TAB_PROPERTY_NAME = 'tab_include_parts';
export const WORK_ITEM_PARTS_TAB_PROPERTY_NAME = 'tab_include_work_item_parts';
export const PART_FILES_TAB_PROPERTY_NAME = 'tab_include_part_files';

export const tabPropertyEnabled = (all_tabs, tab, name, defaultOn = false) => {
  if (!tab) return true;
  const tabRecord = (all_tabs || []).find(t => t.name === tab) || {};
  const property = (tabRecord.tab_properties || []).find(p => p.name === name);
  if (!property) return defaultOn;
  return property.value === undefined || property.value === null || String(property.value) !== 'false';
};

export const tabPartsEnabled = (all_tabs, tab) => tabPropertyEnabled(all_tabs, tab, PARTS_TAB_PROPERTY_NAME);

export const tabWorkItemPartsEnabled = (all_tabs, tab) => tabPropertyEnabled(all_tabs, tab, WORK_ITEM_PARTS_TAB_PROPERTY_NAME, true);

export const tabPartFilesEnabled = (all_tabs, tab) => tabPropertyEnabled(all_tabs, tab, PART_FILES_TAB_PROPERTY_NAME);
